<template>
    <central-modal
        header="Site Settings"
        v-model:visible="isModalVisible"
        size="medium"
    >
        <div
            class="settings-view-wrapper"
            :class="{ 'is-mobile': isMobileView }"
        >
            <nav
                v-if="!isMobileView"
                class="settings-side-nav border-right"
            >
                <primevue-button
                    :class="{ selected: settingsNavOption.id === selectedSettingsNavOption.id }"
                    :key="settingsNavOption.id"
                    @click="updateCurrentSettingsModule(settingsNavOption)"
                    class="settings-side-nav-button"
                    text
                    :disabled="!hasAccess(settingsNavOption.id)"
                    v-for="settingsNavOption in settingsNavOptions"
                >
                    <font-awesome-icon :icon="['fas', settingsNavOption.icon]" class="section-icon" />
                    {{ settingsNavOption.name }}
                    <disabled-icon class="disabled-icon" v-if="!hasAccess(settingsNavOption.id)" />
                </primevue-button>
            </nav>
            <div class="content">
                <div
                    v-if="isMobileView"
                    class="mobile-settings-nav"
                >
                    <nested-nav-switcher
                        v-model="nestedNavSelection"
                        :items="settingsNestedNavItems"
                        aria-label="Site settings section"
                        drawer-header="Site Settings"
                    />
                </div>
                <general-site-settings v-if="selectedSettingsNavOption.id === 'general'" />
                <power-markets-settings
                    ref="powerSettings"
                    v-if="selectedSettingsNavOption.id === 'markets'"
                    v-model:tab="selectedMarketsTab"
                />
                <storage-tracking-settings v-if="selectedSettingsNavOption.id === 'assets'" />
            </div>
        </div>
    </central-modal>
</template>

<script lang="ts" setup>
import PrimevueButton from 'primevue/button';
import { computed, ref, watch } from 'vue';
import { useIndexStore } from '@/stores/benchmarks/indexStore.ts';
import { useRoute } from 'vue-router';
import { useUserAccessStore } from '@/stores/userAccessStore.ts';
import { enactRoutes } from '@/routes/enactRoutes.ts';
import { useUIStore } from '@/stores/uiStore.ts';
import { storeToRefs } from 'pinia';
import {
    PowerMarketsSettingsTab,
    SiteSettingsSection,
} from '@/types/siteSettings/SiteSettingsTypes.ts';
import { INestedNavItem } from '@/interfaces/general/INestedNavItem.ts';
import CentralModal from '../common/CentralModal.vue';
import NestedNavSwitcher from '../common/NestedNavSwitcher.vue';
import PowerMarketsSettings from './PowerMarketsSettings.vue';
import GeneralSiteSettings from './GeneralSiteSettings.vue';
import StorageTrackingSettings from './StorageTrackingSettings.vue';
import DisabledIcon from '../powerMarkets/common/DisabledIcon.vue';

interface ISettingsNavInfo {
    name: string;
    id: SiteSettingsSection;
    icon: string;
    routes: Array<string>;
}

interface IMarketsTabNavInfo {
    name: string;
    id: PowerMarketsSettingsTab;
}

const MARKETS_NAV_PREFIX = 'markets:';

const marketsTabOptions: Array<IMarketsTabNavInfo> = [
    { name: 'General', id: 'general' },
    { name: 'Time/Period', id: 'timePeriod' },
    { name: 'BOA', id: 'boa' },
    { name: 'Charts and Tables', id: 'chartsAndTables' },
    { name: 'Plants', id: 'plants' },
    { name: 'Pools', id: 'pools' },
    { name: 'Heartbeat', id: 'heartbeat' },
];

const powerSettings = ref<InstanceType<typeof PowerMarketsSettings> | null>(null);
const indexStore = useIndexStore();
const hasPoolAccess = computed(() => indexStore.userCustomAccessIndexCountries.some(country => country.accessKey === 'Gb'));
const route = useRoute();
const userAccessStore = useUserAccessStore();
const uiStore = useUIStore();
const { siteSettingsModalRequest, isMobileView } = storeToRefs(uiStore);

const settingsNavOptions: Array<ISettingsNavInfo> = [{
    name: 'General',
    id: 'general',
    icon: 'cog',
    routes: [],
},
{
    name: 'Markets',
    id: 'markets',
    icon: 'bolt',
    routes: [enactRoutes.markets.path, enactRoutes.benchmarks.path],
},
{
    name: 'Assets',
    id: 'assets',
    icon: 'industry-windows',
    routes: [enactRoutes.assets.path],
}];

const selectedSettingsNavOption = ref<ISettingsNavInfo>(settingsNavOptions[0]);
const selectedMarketsTab = ref<PowerMarketsSettingsTab>('general');
const isModalVisible = defineModel<boolean>();

const settingsNestedNavItems = computed<Array<INestedNavItem>>(() => {
    const items: Array<INestedNavItem> = [];

    settingsNavOptions.forEach((option) => {
        if (!hasAccess(option.id)) return;

        if (option.id === 'markets') {
            items.push({
                id: 'markets',
                name: option.name,
                children: marketsTabOptions.filter(tab => tab.id !== 'pools' || hasPoolAccess.value).map(tab => ({
                    id: `${MARKETS_NAV_PREFIX}${tab.id}`,
                    name: tab.name,
                })),
            });
            return;
        }

        items.push({
            id: option.id,
            name: option.name,
        });
    });

    return items;
});

const nestedNavSelection = computed<string>({
    get (): string {
        if (selectedSettingsNavOption.value.id === 'markets') {
            return `${MARKETS_NAV_PREFIX}${selectedMarketsTab.value}`;
        }
        return selectedSettingsNavOption.value.id;
    },
    set (value: string): void {
        updateNestedNavSelection(value);
    },
});

function hasAccess (id: string): boolean {
    switch (id) {
        case 'markets':
            return userAccessStore.shortTermMarketsConfig?.hasPaidShortTermMarketsServices ?? false;
        case 'assets':
            return userAccessStore.hasAccess(userAccessStore.storageTrackingConfig);
        case 'general':
            return true;
        default:
            return false;
    }
}

function resolveMarketsTab (requestedTab?: string | null): PowerMarketsSettingsTab {
    const matchedTab = marketsTabOptions.find(tab => tab.id === requestedTab && (tab.id !== 'pools' || hasPoolAccess.value));
    return matchedTab?.id ?? 'general';
}

async function updateCurrentSettingsModule (newSettingsNavOption: ISettingsNavInfo): Promise<void> {
    if (newSettingsNavOption.id === selectedSettingsNavOption.value.id) return;
    if (!(await powerSettings.value?.requestLeave() ?? true)) return;
    selectedSettingsNavOption.value = newSettingsNavOption;
}

async function updateNestedNavSelection (selectionId: string): Promise<void> {
    if (selectionId === nestedNavSelection.value) return;
    if (!(await powerSettings.value?.requestLeave() ?? true)) return;
    if (selectionId.startsWith(MARKETS_NAV_PREFIX)) {
        const marketsOption = settingsNavOptions.find(option => option.id === 'markets');
        if (!marketsOption || !hasAccess('markets')) return;

        selectedSettingsNavOption.value = marketsOption;
        selectedMarketsTab.value = resolveMarketsTab(selectionId.slice(MARKETS_NAV_PREFIX.length));
        return;
    }

    const settingsOption = settingsNavOptions.find(option => option.id === selectionId);
    if (!settingsOption || !hasAccess(settingsOption.id)) return;

    selectedSettingsNavOption.value = settingsOption;
}

watch(() => [
    route.path,
    isModalVisible.value,
    siteSettingsModalRequest.value?.section,
    siteSettingsModalRequest.value?.tab,
], async () => {
    if (!(await powerSettings.value?.requestLeave() ?? true)) return;
    const requestedSidenavInfo = settingsNavOptions.find(
        option => option.id === siteSettingsModalRequest.value?.section,
    );
    if (requestedSidenavInfo && hasAccess(requestedSidenavInfo.id)) {
        selectedSettingsNavOption.value = requestedSidenavInfo;
        if (requestedSidenavInfo.id === 'markets') {
            selectedMarketsTab.value = resolveMarketsTab(siteSettingsModalRequest.value?.tab);
        }
        return;
    }

    const sidenavInfo = settingsNavOptions.find(
        option => option.routes.find(
            pathKey => route.path.split('/').some(routeSection => pathKey === routeSection),
        ) !== undefined,
    );
    selectedSettingsNavOption.value = (!sidenavInfo || !hasAccess(sidenavInfo.id))
        ? settingsNavOptions[0]
        : sidenavInfo;
});

</script>

<style lang="scss" scoped>
.settings-view-wrapper {
    display: grid;
    grid-template-rows: auto 1fr;
    grid-template-columns: auto minmax(0, 1fr);
    grid-template-areas: "header header" "sidebar content";
    height: 100%;
    overflow-x: hidden;

    &.is-mobile {
        grid-template-columns: 1fr;
        grid-template-areas: "content";
    }

    .header {
        grid-area: header;
    }

    .settings-side-nav {
        background: var(--neutral100);
        height: 100%;
        display: flex;
        flex-direction: column;
        grid-area: sidebar;
        border-bottom-left-radius: var(--p-dialog-border-radius);
    }

    .content {
        grid-area: content;
        min-width: 0;
        overflow: hidden;
        background-color: var(--neutral100);
        display: flex;
        flex-direction: column;
        min-height: 0;

        > :not(.mobile-settings-nav) {
            flex: 1;
            min-height: 0;
            overflow: hidden;
        }
    }

    .mobile-settings-nav {
        flex-shrink: 0;
        padding: var(--smallSpacing);
        background-color: var(--neutral0);
    }

    .settings-side-nav-button {
        display: flex;
        gap: var(--defaultSpacing);
        border-radius: 0px;
        padding: var(--extraLargeSpacing);
        border-left: solid 2px transparent;
        justify-content: flex-start;
        color: var(--primaryFont);

        .section-icon {
            font-size: var(--extraLargeFontSize);
            margin-right: var(--defaultSpacing);
        }

        &.selected {
            background-color: var(--neutral300);
            border-left: solid 2px var(--accentColour);
        }
    }
}
</style>
